/**
 * SC-06 — does this prompt ask to rephrase the current slide? Spec: rephrase/rewrite/tighten/simplify/shorten/
 * punch up + slide|bullets|content; trailing words ("…for a board audience") ride along to the model.
 * AIVA-9184 adds "improve" and "make this slide better". This is only the fast path: a phrasing it misses
 * still reaches SC-06 when the v2 router picks ppt_rephrase_slide and hands the turn back.
 */

// "help me" (AIVA-9184): "can you help me improve the slide?", "help me rewrite the content".
const POLITE_PREFIX =
  String.raw`(?:(?:please|pls|hey aiva|aiva)[,\s]+)?(?:(?:can|could|would) you\s+)?(?:please\s+)?` +
  String.raw`(?:help\s+me\s+(?:to\s+)?)?`;
const VERB = String.raw`(?:rephrase|rewrite|re-write|reword|tighten(?:\s+up)?|simplify|shorten|punch\s+up|improve)`;
// slide|bullets|content within a few words of the verb; "slide 4" is another slide, so it doesn't count.
const NOUN = String.raw`(?:slide(?!\s*\d)|bullets?|bullet\s+points?|points|content|text|wording)`;
const NEAR = String.raw`(?:[\w'’-]+\s+){0,4}?`;
const TARGET = String.raw`(?:this|the|these|my|the current|my current)\s+(?:slide|bullets?|bullet\s+points?|points|content|text|wording)`;
const ADJECTIVE = String.raw`(?:punchier|clearer|tighter|shorter|simpler|crisper|more\s+concise)`;
const TRAILER = String.raw`(?:[,\s]+please)?\s*[?.!]*\s*`;

const PATTERNS: readonly RegExp[] = [
  // rewrite this slide · tighten these bullets for a board audience · punch up the bullet points · improve the slide
  new RegExp(String.raw`^\s*${POLITE_PREFIX}${VERB}\s+${NEAR}${NOUN}\b`, "i"),
  // make this punchier · make it clearer · make the bullets shorter (spec phrasings without a rewrite verb)
  new RegExp(String.raw`^\s*${POLITE_PREFIX}make\s+(?:this|it|${TARGET})\s+${ADJECTIVE}${TRAILER}$`, "i"),
  // make this slide better — named slide text only: "make it better" may mean the last draft in the chat.
  new RegExp(String.raw`^\s*${POLITE_PREFIX}make\s+${TARGET}\s+better${TRAILER}$`, "i"),
];

// SC-06 rewrites text only; "improve the slide design" is a design request (Deckorate), left to the router.
const DESIGN_WORDS = new RegExp(
  String.raw`\b(?:design|layout|look|colou?rs?|theme|fonts?|images?|pictures?|charts?|icons?|alignment|` +
    String.raw`animations?|transitions?|branding)\b`,
  "i",
);

export function matchRephraseSlideIntent(prompt: string): boolean {
  const text = prompt.trim();
  if (!text || text.startsWith("/")) return false;
  return PATTERNS.some((re) => re.test(text)) && !DESIGN_WORDS.test(text);
}
